/** Max images per message. Mirrored on the agent and admin services. */
export const MAX_IMAGES = 3

/** Longest edge after downscaling. Enough to read a build error or dashboard. */
const MAX_EDGE = 1280
/** Roughly the Firestore document budget once base64 inflation is counted. */
const MAX_DATA_URL = 900_000

export type PickedImage = { url: string; name: string }

/**
 * Reads an image file and downscales it to a bounded JPEG data URL.
 *
 * Downscaling happens in the browser for three reasons: a phone screenshot is
 * several megabytes and would be slow to upload, images are billed to the
 * model as tokens, and they are stored inline in Firestore which caps a
 * document at about 1MiB.
 */
export const fileToDataUrl = (file: File): Promise<PickedImage> =>
  new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error(`${file.name} is not an image.`))
      return
    }
    const reader = new FileReader()
    reader.onerror = () => reject(new Error(`Could not read ${file.name}.`))
    reader.onload = () => {
      const img = new Image()
      img.onerror = () => reject(new Error(`Could not decode ${file.name}.`))
      img.onload = () => {
        const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height))
        const w = Math.max(1, Math.round(img.width * scale))
        const h = Math.max(1, Math.round(img.height * scale))
        const canvas = document.createElement('canvas')
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('Could not process the image.'))
          return
        }
        ctx.drawImage(img, 0, 0, w, h)

        // Step the quality down until it fits, rather than rejecting outright.
        let quality = 0.82
        let url = canvas.toDataURL('image/jpeg', quality)
        while (url.length > MAX_DATA_URL && quality > 0.4) {
          quality -= 0.12
          url = canvas.toDataURL('image/jpeg', quality)
        }
        if (url.length > MAX_DATA_URL) {
          reject(new Error(`${file.name} is too large even after resizing.`))
          return
        }
        resolve({ url, name: file.name })
      }
      img.src = String(reader.result)
    }
    reader.readAsDataURL(file)
  })

/** Reads a selection, keeping within the per-message cap. */
export const pickImages = async (
  files: FileList | File[],
  alreadyPicked: number
): Promise<{ images: PickedImage[]; error: string | null }> => {
  const room = MAX_IMAGES - alreadyPicked
  if (room <= 0) {
    return { images: [], error: `You can attach up to ${MAX_IMAGES} images.` }
  }
  const chosen = Array.from(files).slice(0, room)
  const skipped = Array.from(files).length - chosen.length
  const out: PickedImage[] = []
  let error: string | null =
    skipped > 0 ? `Only ${MAX_IMAGES} images per message.` : null
  for (const f of chosen) {
    try {
      out.push(await fileToDataUrl(f))
    } catch (e) {
      error = (e as Error).message
    }
  }
  return { images: out, error }
}
